'use client'

import { useCallback, useEffect, useMemo, useReducer, useState } from 'react'

import { DebugPanel } from './components'
import { DebugContext } from './context'
import {
	useConsoleInterceptor,
	useHydrationErrorInterceptor,
	useNetworkInterceptor,
	useUserActionTracker
} from './hooks'
import type { DebugContextValue, DebugState, HydrationError, LogEntry, NetworkRequest, UserAction } from './types'
import { generateId, isDebuggerEnabled } from './utils'

const MAX_LOGS = 200
const MAX_NETWORK_REQUESTS = 100
const MAX_USER_ACTIONS = 50
const MAX_HYDRATION_ERRORS = 50

type DebugAction =
	| { type: 'ADD_LOG'; payload: LogEntry }
	| { type: 'ADD_NETWORK_REQUEST'; payload: NetworkRequest }
	| { type: 'UPDATE_NETWORK_REQUEST'; payload: { id: string; updates: Partial<NetworkRequest> } }
	| { type: 'ADD_USER_ACTION'; payload: UserAction }
	| { type: 'ADD_HYDRATION_ERROR'; payload: HydrationError }
	| { type: 'CLEAR_LOGS' }
	| { type: 'CLEAR_NETWORK_REQUESTS' }
	| { type: 'CLEAR_USER_ACTIONS' }
	| { type: 'CLEAR_HYDRATION_ERRORS' }
	| { type: 'CLEAR_ALL' }

const initialState: DebugState = {
	logs: [],
	networkRequests: [],
	userActions: [],
	hydrationErrors: [],
	isEnabled: false
}

const debugReducer = (state: DebugState, action: DebugAction): DebugState => {
	switch (action.type) {
		case 'ADD_LOG':
			return {
				...state,
				logs: [action.payload, ...state.logs].slice(0, MAX_LOGS)
			}

		case 'ADD_NETWORK_REQUEST':
			return {
				...state,
				networkRequests: [action.payload, ...state.networkRequests].slice(0, MAX_NETWORK_REQUESTS)
			}

		case 'UPDATE_NETWORK_REQUEST':
			return {
				...state,
				networkRequests: state.networkRequests.map(req =>
					req.id === action.payload.id ? { ...req, ...action.payload.updates } : req
				)
			}

		case 'ADD_USER_ACTION':
			return {
				...state,
				userActions: [action.payload, ...state.userActions].slice(0, MAX_USER_ACTIONS)
			}

		case 'CLEAR_LOGS':
			return { ...state, logs: [] }

		case 'CLEAR_NETWORK_REQUESTS':
			return { ...state, networkRequests: [] }

		case 'CLEAR_USER_ACTIONS':
			return { ...state, userActions: [] }

		case 'ADD_HYDRATION_ERROR':
			return {
				...state,
				hydrationErrors: [action.payload, ...state.hydrationErrors].slice(0, MAX_HYDRATION_ERRORS)
			}

		case 'CLEAR_HYDRATION_ERRORS':
			return { ...state, hydrationErrors: [] }

		case 'CLEAR_ALL':
			return { ...state, logs: [], networkRequests: [], userActions: [], hydrationErrors: [] }

		default:
			return state
	}
}

interface DebugProviderProps {
	children: React.ReactNode
}

const DebugProviderInternal = ({ children }: DebugProviderProps): JSX.Element => {
	const [state, dispatch] = useReducer(debugReducer, initialState)

	const addLog = useCallback((entry: Omit<LogEntry, 'id' | 'timestamp'>) => {
		dispatch({
			type: 'ADD_LOG',
			payload: {
				...entry,
				id: generateId(),
				timestamp: Date.now()
			}
		})
	}, [])

	const addNetworkRequest = useCallback((request: Omit<NetworkRequest, 'id' | 'timestamp'>) => {
		const id = generateId()
		dispatch({
			type: 'ADD_NETWORK_REQUEST',
			payload: {
				...request,
				id,
				timestamp: Date.now()
			}
		})
		return id
	}, [])

	const networkRequestIdMap = useMemo(() => new Map<string, string>(), [])

	const addNetworkRequestWithId = useCallback(
		(request: Omit<NetworkRequest, 'id' | 'timestamp'>): string => {
			const id = generateId()
			networkRequestIdMap.set(request.url + Date.now(), id)
			dispatch({
				type: 'ADD_NETWORK_REQUEST',
				payload: {
					...request,
					id,
					timestamp: Date.now()
				}
			})
			return id
		},
		[networkRequestIdMap]
	)

	const updateNetworkRequest = useCallback((id: string, updates: Partial<NetworkRequest>) => {
		dispatch({
			type: 'UPDATE_NETWORK_REQUEST',
			payload: { id, updates }
		})
	}, [])

	const addUserAction = useCallback((action: Omit<UserAction, 'id' | 'timestamp'>) => {
		dispatch({
			type: 'ADD_USER_ACTION',
			payload: {
				...action,
				id: generateId(),
				timestamp: Date.now()
			}
		})
	}, [])

	const addHydrationError = useCallback((error: Omit<HydrationError, 'id' | 'timestamp'>) => {
		dispatch({
			type: 'ADD_HYDRATION_ERROR',
			payload: {
				...error,
				id: generateId(),
				timestamp: Date.now()
			}
		})
	}, [])

	const clearLogs = useCallback(() => dispatch({ type: 'CLEAR_LOGS' }), [])
	const clearNetworkRequests = useCallback(() => dispatch({ type: 'CLEAR_NETWORK_REQUESTS' }), [])
	const clearUserActions = useCallback(() => dispatch({ type: 'CLEAR_USER_ACTIONS' }), [])
	const clearHydrationErrors = useCallback(() => dispatch({ type: 'CLEAR_HYDRATION_ERRORS' }), [])
	const clearAll = useCallback(() => dispatch({ type: 'CLEAR_ALL' }), [])

	useConsoleInterceptor({ onLog: addLog })
	useNetworkInterceptor({
		onRequest: addNetworkRequestWithId,
		onRequestUpdate: updateNetworkRequest
	})
	useUserActionTracker({ onAction: addUserAction })
	useHydrationErrorInterceptor({ onHydrationError: addHydrationError })

	const contextValue: DebugContextValue = useMemo(
		() => ({
			state,
			addLog,
			addNetworkRequest,
			updateNetworkRequest,
			addUserAction,
			addHydrationError,
			clearLogs,
			clearNetworkRequests,
			clearUserActions,
			clearHydrationErrors,
			clearAll
		}),
		[
			state,
			addLog,
			addNetworkRequest,
			updateNetworkRequest,
			addUserAction,
			addHydrationError,
			clearLogs,
			clearNetworkRequests,
			clearUserActions,
			clearHydrationErrors,
			clearAll
		]
	)

	return (
		<DebugContext.Provider value={contextValue}>
			{children}
			<DebugPanel />
		</DebugContext.Provider>
	)
}

export const DebugProvider = ({ children }: DebugProviderProps): JSX.Element => {
	const [isEnabled, setIsEnabled] = useState(false)

	useEffect(() => {
		setIsEnabled(isDebuggerEnabled())
	}, [])

	if (!isEnabled) {
		return <>{children}</>
	}

	return <DebugProviderInternal>{children}</DebugProviderInternal>
}
